import React from 'react';
import { ActionButton } from 'equal-ds-ui';
import { X, Info } from 'lucide-react';
import type { AdminUserTableRow } from '../../../types/admin-users';
import approveTickIcon from '../../../assets/images/approve_tick_icon.png';
import redCrossIcon from '../../../assets/images/red_cross_icon.png';

export interface AdminUserStatusDialogProps {
  user: AdminUserTableRow | null;
  nextStatus: string;
  isOpen: boolean;
  onConfirm: () => Promise<void> | void;
  onClose: () => void;
  isProcessing?: boolean;
}

/** Map nextStatus codes to dialog variants */
function getDialogConfig(nextStatus: string) {
  const normalized = nextStatus.toLowerCase();

  if (normalized === 'activate' || normalized === '1' || normalized === 'active') {
    return {
      title: 'Activate User',
      actionVerb: 'activate',
      icon: approveTickIcon,
      buttonLabel: 'Activate',
      intent: 'approve' as const,
    };
  }

  if (normalized === 'unlock' || normalized === '3') {
    return {
      title: 'Unlock User',
      actionVerb: 'unlock',
      icon: approveTickIcon,
      buttonLabel: 'Unlock',
      intent: 'approve' as const,
    };
  }

  // Default: Deactivate
  return {
    title: 'Deactivate User',
    actionVerb: 'deactivate',
    icon: redCrossIcon,
    buttonLabel: 'Deactivate',
    intent: 'reject' as const,
  };
}

export const AdminUserStatusDialog: React.FC<AdminUserStatusDialogProps> = ({
  user,
  nextStatus,
  isOpen,
  onConfirm,
  onClose,
  isProcessing = false,
}) => {
  if (!isOpen || !user) {
    return null;
  }

  const config = getDialogConfig(nextStatus);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-md bg-white rounded-xl shadow-xl border border-border-default p-6">
        {/* Header with close button */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-text-primary">
            {config.title}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-gray-50 hover:bg-gray-200 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Icon */}
        <div className="flex justify-center mb-5">
          <img src={config.icon} alt="" className="w-20 h-20 object-contain" />
        </div>

        {/* Message */}
        <p className="text-sm text-text-secondary text-center mb-2">
          Are you sure you want to {config.actionVerb}{' '}
          <span className="font-semibold text-text-primary">{user.name}</span>?
        </p>

        {/* Warning note */}
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 mb-6">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>Please note that this action cannot be undone.</span>
        </div>

        {/* Action button */}
        <div className="equal-ds-root">
          <ActionButton
            intent={config.intent}
            size="md"
            filled
            onClick={onConfirm}
            disabled={isProcessing}
            className="w-full"
          >
            {isProcessing ? 'Processing...' : config.buttonLabel}
          </ActionButton>
        </div>
      </div>
    </div>
  );
};
